import React, { useEffect, useMemo, useState } from "react";
import {
  calculateGCodeProgram,
  CNC_OPERATION_OPTIONS,
  CNC_MACHINE_PROFILES,
} from "../../utils/calculations";
import useCalculatorMode from "../../hooks/useCalculatorMode";
import useCalculatorResults from "../../hooks/useCalculatorResults";
import CalculationImportPanel from "../CalculationImportPanel";
import { getRecords } from "../../utils/shopDataStore";

const GCodeCalculator = ({ onSave }) => {
  const [params, setParams] = useState({
    machineProfile: "haas_vf2",
    machineYear: 2020,
    operation: "face_mill",
    stockX: 4,
    stockY: 2,
    stockZ: 1,
    toolNumber: 1,
    toolDiameter: 0.5,
    spindleRPM: 3000,
    feedRate: 24,
    depth: 0.03,
    safeZ: 0.25,
    holeCount: 4,
    threadPitch: 0.05,
    importedCoordinates: [],
    lineage: "",
    safeStartBlock: "G20",
    workOffset: "G54",
    coolantCode: "M08",
    planeSelection: "G17",
    distanceMode: "G90",
    retractPlane: 0.05,
    peckDepth: "",
    shopToolId: "",
    shopMachineId: "",
    shopMachineName: "",
    shopMachineMaxRPM: "",
  });
  const [results, setResults] = useState(null);
  const { isAdvanced, mode, toggleMode } = useCalculatorMode();
  const { saveResult } = useCalculatorResults("gcode", "imperial");
  const shopTools = useMemo(() => getRecords("tools").filter((tool) => tool.status !== "Inactive"), []);
  const shopMachines = useMemo(() => getRecords("machines").filter((machine) => machine.status !== "Inactive"), []);

  useEffect(() => {
    setResults(calculateGCodeProgram(params));
  }, [params]);

  useEffect(() => {
    if (!results || results.errors) return;
    saveResult({
      calculatorId: "gcode",
      calculatorName: "G-code Starter",
      inputValues: params,
      outputValues: {
        program: results.program,
        machine: `${results.machine.brand} ${results.machine.model}`,
        operation: results.operation?.label,
      },
      units: "imperial",
      operationType: params.operation,
      warnings: results.warnings || [],
      sourceFormulaReferences: ["Machine-aware starter G-code template; verify against controller/post requirements."],
      lineage: params.lineage ? [params.lineage] : [],
    });
  }, [results, params, saveResult]);

  const selectedMachine = CNC_MACHINE_PROFILES.find(
    (machine) => machine.value === params.machineProfile
  ) || CNC_MACHINE_PROFILES[0];
  const operationOptions = CNC_OPERATION_OPTIONS.filter(
    (operation) => operation.type === selectedMachine.type
  );

  const handleChange = (field, value) => {
    setParams((previous) => {
      if (field === "machineProfile") {
        const nextMachine = CNC_MACHINE_PROFILES.find((machine) => machine.value === value);
        const nextOperations = CNC_OPERATION_OPTIONS.filter((operation) => operation.type === nextMachine?.type);
        const operationStillValid = nextOperations.some((operation) => operation.value === previous.operation);
        return {
          ...previous,
          machineProfile: value,
          operation: operationStillValid ? previous.operation : nextOperations[0]?.value || previous.operation,
        };
      }

      return { ...previous, [field]: value };
    });
  };

  const handleNumber = (field, value) => {
    setParams((previous) => ({ ...previous, [field]: value === "" ? "" : parseFloat(value) }));
  };

  const selectShopTool = (toolId) => {
    const tool = shopTools.find((item) => item.id === toolId);
    setParams((previous) => ({
      ...previous,
      shopToolId: toolId,
      toolDiameter: tool?.diameter || previous.toolDiameter,
    }));
  };

  const selectShopMachine = (machineId) => {
    const machine = shopMachines.find((item) => item.id === machineId);
    setParams((previous) => ({
      ...previous,
      shopMachineId: machineId,
      shopMachineName: machine?.name || "",
      shopMachineMaxRPM: machine?.spindleRpm || "",
    }));
  };

  const handleSave = () => {
    if (!results || results.errors || !onSave) return;
    onSave({
      summary: `${results.operation?.label || params.operation} | ${results.machine.brand} ${results.machine.model}`,
      input: params,
      results,
      warnings: results.warnings,
      confidence: results.confidence,
      lineage: params.lineage ? [params.lineage] : [],
    });
  };

  const copyToClipboard = () => {
    if (!results?.program) return;
    navigator.clipboard.writeText(results.program);
  };

  const hasErrors = results?.errors && Object.keys(results.errors).length > 0;

  return (
    <div className="calculator-page">
      <div className="calculator-shell">
        <div className="calculator-header" style={{ marginBottom: "24px" }}>
          <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
            G-code Starter Calculator
          </h1>
          <p style={{ color: "var(--text-muted)", margin: 0 }}>
            Generate a machine-aware starter program from brand, model, year,
            operation, stock size, and cutting data.
          </p>
        </div>

        <div className="calculator-layout calculator-layout-wide-results">
          <div className="calculator-panel">
            <div className="calculator-mode-row">
              <span>{mode === "advanced" ? "Advanced Mode" : "Beginner Mode"}</span>
              <button type="button" className="btn-secondary" onClick={toggleMode}>
                {isAdvanced ? "Use Beginner Mode" : "Use Advanced Mode"}
              </button>
            </div>
            <CalculationImportPanel
              targetCalculatorId="gcode"
              targetUnits="imperial"
              onApply={({ values, lineage }) => {
                setParams((previous) => ({
                  ...previous,
                  ...values,
                  operation: values.importedCoordinates?.length && ["spot_drill_pattern", "drill_pattern", "peck_drill_pattern", "tap_pattern"].includes(previous.operation)
                    ? previous.operation
                    : values.importedCoordinates?.length
                      ? "drill_pattern"
                      : previous.operation,
                  holeCount: values.importedCoordinates?.length || values.holeCount || previous.holeCount,
                  lineage,
                }));
              }}
            />
            {shopTools.length > 0 && (
              <div className="form-group">
                <label>Tool Library Tool</label>
                <select value={params.shopToolId} onChange={(event) => selectShopTool(event.target.value)}>
                  <option value="">Manual tool inputs</option>
                  {shopTools.map((tool) => <option key={tool.id} value={tool.id}>{tool.name}</option>)}
                </select>
              </div>
            )}
            {shopMachines.length > 0 && (
              <div className="form-group">
                <label>Shop Machine Capability Record</label>
                <select value={params.shopMachineId} onChange={(event) => selectShopMachine(event.target.value)}>
                  <option value="">No linked shop machine</option>
                  {shopMachines.map((machine) => <option key={machine.id} value={machine.id}>{machine.name}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Machine Brand / Model</label>
              <select
                value={params.machineProfile}
                onChange={(event) => handleChange("machineProfile", event.target.value)}
              >
                {CNC_MACHINE_PROFILES.map((machine) => (
                  <option key={machine.value} value={machine.value}>
                    {machine.brand} {machine.model} - {machine.control}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Machine Year</label>
              <input
                type="number"
                value={params.machineYear}
                onChange={(event) => handleNumber("machineYear", event.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Operation</label>
              <select
                value={params.operation}
                onChange={(event) => handleChange("operation", event.target.value)}
              >
                {operationOptions.map((operation) => (
                  <option key={operation.value} value={operation.value}>
                    {operation.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={machineMetaStyle}>
              <strong>{selectedMachine.type === "lathe" ? "Lathe post mode" : "Mill post mode"}</strong>
              <span>{selectedMachine.control}</span>
            </div>

            <div style={gridStyle}>
              <NumberInput
                label={selectedMachine.type === "lathe" ? "Stock Diameter X (in)" : "Stock X (in)"}
                field="stockX"
                params={params}
                onChange={handleNumber}
              />
              {selectedMachine.type === "mill" && (
                <NumberInput label="Stock Y (in)" field="stockY" params={params} onChange={handleNumber} />
              )}
              <NumberInput
                label={selectedMachine.type === "lathe" ? "Stock Length Z (in)" : "Stock Z (in)"}
                field="stockZ"
                params={params}
                onChange={handleNumber}
              />
              <NumberInput label="Tool Number" field="toolNumber" params={params} onChange={handleNumber} />
              {selectedMachine.type === "mill" && (
                <NumberInput label="Tool Diameter (in)" field="toolDiameter" params={params} onChange={handleNumber} />
              )}
              <NumberInput label="Spindle RPM" field="spindleRPM" params={params} onChange={handleNumber} />
              <NumberInput
                label={selectedMachine.type === "lathe" ? "Feed (in/rev)" : "Feed Rate (IPM)"}
                field="feedRate"
                params={params}
                onChange={handleNumber}
              />
              <NumberInput label={selectedMachine.type === "lathe" ? "Radial Depth / Thread Depth (in)" : "Cut Depth (in)"} field="depth" params={params} onChange={handleNumber} />
              <NumberInput label={selectedMachine.type === "lathe" ? "Clearance (in)" : "Safe Z (in)"} field="safeZ" params={params} onChange={handleNumber} />
              {["spot_drill_pattern", "drill_pattern", "peck_drill_pattern", "tap_pattern"].includes(params.operation) && (
                <NumberInput label="Hole Count" field="holeCount" params={params} onChange={handleNumber} />
              )}
              {params.operation === "thread_od" && (
                <NumberInput label="Thread Pitch / Lead (in)" field="threadPitch" params={params} onChange={handleNumber} />
              )}
            </div>

            {params.importedCoordinates?.length > 0 && (
              <div style={machineMetaStyle}>
                <strong>Imported coordinates</strong>
                <span>{params.importedCoordinates.length} holes from previous calculation</span>
              </div>
            )}

            {isAdvanced && (
              <div className="advanced-options-panel">
                <h3>Advanced Post / Controller Options</h3>
                <div style={gridStyle}>
                  <TextInput label="Safe Start Block" field="safeStartBlock" params={params} onChange={handleChange} />
                  <TextInput label="Work Offset" field="workOffset" params={params} onChange={handleChange} />
                  <div className="form-group">
                    <label>Coolant</label>
                    <select value={params.coolantCode} onChange={(event) => handleChange("coolantCode", event.target.value)}>
                      <option value="M08">Flood M08</option>
                      <option value="M07">Mist M07</option>
                      <option value="off">Off</option>
                    </select>
                  </div>
                  {selectedMachine.type === "mill" && (
                    <>
                      <div className="form-group">
                        <label>Plane Selection</label>
                        <select value={params.planeSelection} onChange={(event) => handleChange("planeSelection", event.target.value)}>
                          <option value="G17">G17 XY</option>
                          <option value="G18">G18 XZ</option>
                          <option value="G19">G19 YZ</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Distance Mode</label>
                        <select value={params.distanceMode} onChange={(event) => handleChange("distanceMode", event.target.value)}>
                          <option value="G90">G90 Absolute</option>
                          <option value="G91">G91 Incremental</option>
                        </select>
                      </div>
                      <NumberInput label="Cycle Retract Plane R (in)" field="retractPlane" params={params} onChange={handleNumber} />
                      <NumberInput label="Peck Depth Q (in)" field="peckDepth" params={params} onChange={handleNumber} />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="calculator-panel">
            {hasErrors ? (
              <div style={errorBoxStyle}>
                {Object.values(results.errors).map((error) => (
                  <div key={error}>{error}</div>
                ))}
              </div>
            ) : results ? (
              <>
                <div style={warningStyle}>
                  <strong>Do not run this directly on a machine.</strong>
                  <br />
                  This is machine-aware starter code, not a certified post. Backplot,
                  simulate, dry run above the part, and verify offsets, tools, syntax,
                  and options against your exact control before cutting.
                </div>

                <pre style={programStyle}>{results.program}</pre>
                {params.lineage && (
                  <div style={lineageStyle}>{params.lineage}</div>
                )}

                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  <button type="button" className="btn-primary" onClick={handleSave}>
                    Save Calculation
                  </button>
                  <button type="button" className="btn-secondary" onClick={copyToClipboard}>
                    Copy G-code
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const NumberInput = ({ label, field, params, onChange }) => (
  <div className="form-group">
    <label>{label}</label>
    <input
      type="number"
      step="0.001"
      value={params[field]}
      onChange={(event) => onChange(field, event.target.value)}
    />
  </div>
);

const TextInput = ({ label, field, params, onChange }) => (
  <div className="form-group">
    <label>{label}</label>
    <input
      type="text"
      value={params[field]}
      onChange={(event) => onChange(field, event.target.value)}
    />
  </div>
);

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: "12px",
};

const machineMetaStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  padding: "12px",
  marginBottom: "14px",
  border: "1px solid var(--border-color)",
  borderRadius: "8px",
  background: "var(--bg-panel)",
  color: "var(--text-secondary)",
  fontSize: "13px",
};

const warningStyle = {
  padding: "14px",
  borderRadius: "10px",
  background: "rgba(245, 158, 11, 0.12)",
  border: "1px solid rgba(245, 158, 11, 0.35)",
  color: "var(--text-secondary)",
  lineHeight: 1.55,
  marginBottom: "16px",
};

const errorBoxStyle = {
  padding: "14px",
  borderRadius: "10px",
  background: "rgba(239, 68, 68, 0.12)",
  border: "1px solid rgba(239, 68, 68, 0.35)",
  color: "#fecaca",
};

const programStyle = {
  minHeight: "360px",
  maxHeight: "620px",
  overflow: "auto",
  whiteSpace: "pre-wrap",
  padding: "16px",
  borderRadius: "10px",
  background: "#020617",
  border: "1px solid var(--border-color)",
  color: "#d1fae5",
  fontSize: "13px",
  lineHeight: 1.55,
};

const lineageStyle = {
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid rgba(34, 211, 238, 0.28)",
  background: "rgba(34, 211, 238, 0.08)",
  color: "var(--text-secondary)",
  marginBottom: "16px",
};

export default GCodeCalculator;
